import { merge, omit } from 'lodash-es';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { Box, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { getRouteTitle, ROUTES } from '../../../constants/RouteNameMapping';
import { GenericConfigPage } from '../Common/GenericConfigPage';

import {
  BasicFormConfig,
  createFieldDataModel,
  createFieldFormConfigDataModel,
  FieldEntity,
  IField,
  KpiConfigScenario,
  KpiConfigSection,
  rendererTypeToPrimitiveType,
} from '../../../data-models/field2.data-model';
import { field3ToFormField } from '../../../data-models/field3.data-model';
import { createFormatterDataModel } from '../../../data-models/formatter.data-model';
import { kpiConfigAtom } from '../../../services/state/KPIConfigState';
import { useGetKpiFields, getAdminKpiFormFields, withoutFormatterField } from './KPIFields';
import { SortKPIs } from './KPISort';

export function getDefaultValues(): Partial<IField<unknown>> {
  return { sectionType: KpiConfigSection.Both, kpiScenario: KpiConfigScenario.Accrued };
}

export const KPIConfigBaseURL = '/kpiConfig';

export function ConfigKPIs() {
  const universalKPIsFields = [...(useAtomValue(kpiConfigAtom) ?? [])].sort(sortKpisByType);
  const kpiFields = useGetKpiFields();
  const rowFields = useMemo(
    () => withoutFormatterField(kpiFields).map((field) => field3ToFormField(field)),
    [kpiFields]
  );

  return (
    <GenericConfigPage
      baseTitle={'KPI'}
      customToolbarElem={
        <Box display={'flex'} columnGap={'.25rem'}>
          <GoToGroupsBtn />
          <SortKPIs />
        </Box>
      }
      data={universalKPIsFields}
      defaultViewMode='grid'
      fields={kpiFields}
      rowFields={rowFields}
      pageTitle={getRouteTitle(ROUTES.CONFIG_KPI)}
      queryURL={KPIConfigBaseURL}
      transformBeforeSubmit={(data) => {
        return transformKpi(data);
      }}
      defaultValues={getDefaultValues()}
      dynamicFormTransformer={getAdminKpiFormFields}
    />
  );
}

export function transformKpi(formData: Partial<IField<unknown>>): IField<unknown> {
  if (!formData.formMeta?.renderer?.type || !formData.displayName) {
    throw new Error('Invalid form data');
  }

  const type = formData.formMeta.renderer.type;

  const partialData = {
    entity: formData.entity,
    displayName: formData.displayName,
    formMeta: createFieldFormConfigDataModel({
      formatter: createFormatterDataModel({
        type: rendererTypeToPrimitiveType(type),
        // Keep the admin's chosen Format; fall back to the id the Type derives.
        id: formData.formMeta.formatter?.id || type,
      }),
      renderer: {
        type,
        id: type,
        config: (formData.formMeta.renderer.config ?? {}) as BasicFormConfig,
      },
    }),
    gridMeta: {
      aggFunc: formData.gridMeta?.aggFunc ?? null,
      group: formData.gridMeta?.group,
    },
    type: rendererTypeToPrimitiveType(type),
  };

  return merge(omit(createFieldDataModel(), ['id']), { ...formData }, partialData) as IField<unknown>;
}

export function isSystemKPI(field: FieldValues): boolean {
  return field.entity === FieldEntity.systemKPI;
}

export function sortKpisByType(a: IField<unknown>, b: IField<unknown>): number {
  if (isSystemKPI(a) && !isSystemKPI(b)) {
    return 1;
  } else if (!isSystemKPI(a) && isSystemKPI(b)) {
    return -1;
  } else {
    return a.displayName.localeCompare(b.displayName);
  }
}

export function GoToGroupsBtn() {
  const navigate = useNavigate();

  return (
    <Button
      color={'secondary'}
      onClick={() => {
        navigate(`/${ROUTES.ADMIN_PANEL}/${ROUTES.CONFIG}/${ROUTES.CONFIG_KPI_GROUPS}`);
      }}
      variant={'contained'}
    >
      Manage Groups
    </Button>
  );
}
